import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useApi } from '../../hooks/useApi.js';
import { fmtFechaDisplay } from '../../lib/dates.js';
import type { TipoAusencia } from '../tipos-ausencia/types.js';
import { fmtAnios, type ResumenServicioEmpleado, type SaldoNomina, type VacacionesAllocation } from './types.js';

// Saldo paralelo de nómina — solo lectura, lo calcula el API al vuelo.
function SeccionNomina({ fechaIngresoNomina, nomina }: { fechaIngresoNomina: string | null; nomina: SaldoNomina | null }) {
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)' }}>Escenario nómina</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3, marginBottom: 12 }}>
        {!fechaIngresoNomina || !nomina
          ? 'Sin fecha de ingreso nómina registrada'
          : `Ingreso nómina ${fmtFechaDisplay(fechaIngresoNomina)} — ${fmtAnios(nomina.antiguedadAnios)} de antigüedad nómina — ${nomina.diasTotales} días totales, ${nomina.diasTomados} tomados, ${nomina.diasRestantes} restantes`}
      </div>
      {nomina && (
        <div className="table-wrap">
          <div className="table-scroll">
            <table className="main-table">
              <thead>
                <tr>
                  <th>Aniversario nómina</th>
                  <th>Vigente hasta</th>
                  <th>Días totales</th>
                  <th>Tomados</th>
                  <th>Restantes</th>
                </tr>
              </thead>
              <tbody>
                {nomina.periodos.length === 0 && nomina.adelantoPendiente === 0 ? (
                  <tr>
                    <td className="empty-td" colSpan={5}>Todavía no cumple su primer aniversario en nómina</td>
                  </tr>
                ) : (
                  <>
                    {nomina.periodos.map(p => (
                      <tr key={p.n}>
                        <td>Aniversario {p.n}</td>
                        <td style={{ color: 'var(--muted)' }}>{fmtFechaDisplay(p.fechaFin)}</td>
                        <td>{p.diasOtorgados}</td>
                        <td>{p.diasTomados}</td>
                        <td>{p.diasOtorgados - p.diasTomados}</td>
                      </tr>
                    ))}
                    {nomina.adelantoPendiente > 0 && (
                      <tr>
                        <td>Adelanto (sin periodo aún)</td>
                        <td style={{ color: 'var(--muted)' }}>—</td>
                        <td>0</td>
                        <td>{nomina.adelantoPendiente}</td>
                        <td style={{ color: 'var(--danger)' }}>-{nomina.adelantoPendiente}</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Prompt BB-4(RH) — Pantalla 2: detalle por empleado, allocations vigentes de
// antigüedad ordenadas FIFO (más vieja primero — orden que ya devuelve
// listarAllocations() por fecha_inicio ascendente, ver
// vacaciones-allocations.service.ts). Reutiliza GET /vacaciones-allocations
// (mismo endpoint que "Ausencias") en vez de crear uno nuevo — el filtro
// estado==='vigente' se hace en cliente, igual que el prompt pide.
export function AniosServicioDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useApi();

  const { data: resumen } = useQuery({
    queryKey: ['vacaciones-allocations-resumen-servicio'],
    queryFn: () => get<ResumenServicioEmpleado[]>('/vacaciones-allocations/resumen-servicio'),
  });
  const empleado = resumen?.find(r => r.usuarioId === id);

  const { data: tipos } = useQuery({
    queryKey: ['tipos-ausencia'],
    queryFn: () => get<TipoAusencia[]>('/tipos-ausencia'),
  });
  const nombreTipo = new Map((tipos ?? []).map(t => [t.id, t.nombre]));

  const { data: allocations, isLoading } = useQuery({
    queryKey: ['vacaciones-allocations', id],
    queryFn: () => get<VacacionesAllocation[]>(`/vacaciones-allocations?usuarioId=${id}`),
    enabled: Boolean(id),
  });
  const vigentes = (allocations ?? []).filter(a => a.estado === 'vigente');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <span className="tk-folio" onClick={() => navigate('/anios-servicio')} style={{ fontSize: 12.5 }}>
          ← Años de Servicio
        </span>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)', marginTop: 6 }}>
          {empleado?.nombre ?? '…'}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
          {empleado?.antiguedadAnios === null || empleado?.antiguedadAnios === undefined
            ? 'Sin fecha de ingreso registrada'
            : `Leadsphere — ${fmtAnios(empleado.antiguedadAnios)} de antigüedad — ${empleado.diasTotales} días totales, ${empleado.diasTomados} tomados, ${empleado.diasRestantes} restantes`}
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Tramo</th>
                <th>Vigente hasta</th>
                <th>Días totales</th>
                <th>Tomados</th>
                <th>Restantes</th>
              </tr>
            </thead>
            <tbody>
              {vigentes.length === 0 && !empleado?.adelantoPendiente ? (
                <tr>
                  <td className="empty-td" colSpan={5}>
                    {isLoading ? 'Cargando…' : 'Sin allocations vigentes'}
                  </td>
                </tr>
              ) : (
                <>
                  {vigentes.map(a => (
                    <tr key={a.id}>
                      <td>{nombreTipo.get(a.tipo_ausencia_id) ?? '—'}</td>
                      <td style={{ color: 'var(--muted)' }}>{fmtFechaDisplay(a.fecha_fin)}</td>
                      <td>{a.dias_otorgados}</td>
                      <td>{a.dias_consumidos}</td>
                      <td>{a.dias_otorgados - a.dias_consumidos}</td>
                    </tr>
                  ))}
                  {empleado && empleado.adelantoPendiente > 0 && (
                    <tr>
                      <td>Adelanto (lo absorbe el siguiente aniversario)</td>
                      <td style={{ color: 'var(--muted)' }}>—</td>
                      <td>0</td>
                      <td>{empleado.adelantoPendiente}</td>
                      <td style={{ color: 'var(--danger)' }}>-{empleado.adelantoPendiente}</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {empleado && <SeccionNomina fechaIngresoNomina={empleado.fechaIngresoNomina} nomina={empleado.nomina} />}
    </div>
  );
}
